import React, { FC, useState } from 'react';
import { useClientTranslation } from '@lib/i18n/client';
import { ViewStackViewWithNavigation } from '@modules/get-slash-internet-card/components/dialog-views/view-stack-view-with-navigation';
import { Image } from '@tuic/web-compositions/shared/Image';
import { Button } from '@tuic/web-compositions/core/Button';
import { Tag } from '@tuic/web-compositions/core/Tag';
import { Alert } from '@tuic/web-compositions/core/Alert';
import { ContentfulDocument, documentToReactTree } from '@tuic/contentful';
import { getPriceExpression } from '@modules/get-slash-internet-card/lib/utils';
import { Locale } from '@tuic/i18n';
import { useViewStack } from '@modules/get-slash-internet-card/components/dialog-views/view-stack';
import { BookingSuccess } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/booking-success';
import { CombinedInternetTariff } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/tariff-listing';
import { DialogBody } from '@modules/get-slash-internet-card/components/dialog-views/dialog-body';
import { RefreshDialogHandler } from '@modules/get-slash-internet-card/components/dialog-view-renderer';
import { CheckboxField } from '@tuic/web-compositions/shared/CheckboxField';
type Props = {
  locale: Locale;
  tariff: CombinedInternetTariff;
  termsOfUseCheckbox?: {
    label: React.ReactNode;
    errorMessage?: string;
  };
  buyTariff: (tariff: CombinedInternetTariff) => Promise<true | string>;
  refreshDialog: RefreshDialogHandler;
};
export const TariffDetails: FC<Props> = ({
  locale,
  tariff,
  termsOfUseCheckbox,
  buyTariff,
  refreshDialog
}) => {
  const {
    imageHero,
    imageHeroMd,
    tag,
    text
  } = tariff;

  // true => checked, false => error, null => not interacted with yet
  const [termsAcceptedState, setTermsAccepted] = useState<boolean | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const {
    t
  } = useClientTranslation();
  const {
    push
  } = useViewStack();
  const name = tariff.title ?? tariff.getSlash.name;
  const priceExpression = getPriceExpression(locale, tariff);
  const handleClick = async () => {
    if (!!termsOfUseCheckbox && termsAcceptedState !== true) {
      setTermsAccepted(false);
      return;
    }
    setPending(true);
    setError(undefined);
    const response = await buyTariff(tariff);
    if (response === true) {
      push(<BookingSuccess refreshDialog={refreshDialog} />);
      return;
    }
    setError(response);
    setPending(false);
  };
  return <DialogBody colored data-sentry-element="DialogBody" data-sentry-component="TariffDetails" data-sentry-source-file="tariff-details.tsx">
      <ViewStackViewWithNavigation title={name} data-sentry-element="ViewStackViewWithNavigation" data-sentry-source-file="tariff-details.tsx">
        <div className="space-y-6">
          {(imageHero || imageHeroMd) && <div className="relative">
              {imageHero &&
          // eslint-disable-next-line jsx-a11y/alt-text
          <Image {...imageHero} className="sm:hidden aspect-16/9" />}
              {imageHeroMd &&
          // eslint-disable-next-line jsx-a11y/alt-text
          <Image {...imageHeroMd} className="hidden sm:block aspect-32/9" />}
              {tag && <div className="absolute right-4 bottom-4">
                  <Tag variant="white">{tag}</Tag>
                </div>}
            </div>}

          {!!termsOfUseCheckbox && <div className='flex flex-row gap-4'>
                <CheckboxField name="termsAccepted" value="true" note={termsOfUseCheckbox.label} checked={termsAcceptedState === true} error={termsAcceptedState === false ? termsOfUseCheckbox.errorMessage || t('Um fortzufahren, müssen die Bedingungen akzeptiert werden.') : undefined} onChange={() => {
            setTermsAccepted(prev => prev === null ? true : !prev);
          }} />
            </div>}

          <div className="space-y-6 sm:space-y-0 sm:flex sm:items-center sm:gap-6">
            {(name || priceExpression) && <div className="flex headline-sm sm:flex-1 sm:gap-2">
                {name && <div className="sm:hidden">{name}</div>}

                {priceExpression && <>
                    <div className="hidden sm:block sm:flex-1 sm:text-right">
                      {t('Preis')}
                    </div>
                    <div className="flex-1 text-right sm:flex-none">
                      {priceExpression}
                    </div>
                  </>}
              </div>}

            <div>
              <Button variant="primary" hasFullWidthOnMobile indicating={pending} onClick={handleClick} data-sentry-element="Button" data-sentry-source-file="tariff-details.tsx">
                {t('Tarif kostenpflichtig buchen')}
              </Button>
            </div>
          </div>
        </div>

        {error && <Alert mode="error" headline={t('Ihr Internettarif konnte nicht gebucht werden.')}>
            {error}
          </Alert>}

        {renderText(text)}
      </ViewStackViewWithNavigation>
    </DialogBody>;
};
const renderText = (text: ContentfulDocument | string | undefined) => {
  if (typeof text === 'undefined') {
    return null;
  }
  if (typeof text === 'string') {
    return <p>{text}</p>;
  }
  return <div className="cf-rich-text" data-sentry-component="renderText" data-sentry-source-file="tariff-details.tsx">{documentToReactTree(text)}</div>;
};